import {
  Component,
  ElementRef,
  Input, OnChanges, OnDestroy,
  OnInit, SimpleChanges,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subject } from 'rxjs';

import { SliderValue } from '../../models/slider-value';
import { debounceTime, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'mcrai-slider',
  template: `<div class="mcrai-slider__container" #container>
  <div class="mcrai-slider__track-container">
    <div class="mcrai-slider__track mcrai-slider__track--background"></div>
    <div class="mcrai-slider__track mcrai-slider__track--fill" [ngStyle]="trackStyles"></div>
  </div>
  <div class="mcrai-slider__thumb-container" [style.left.px]="trackOffset">
    <div
      class="mcrai-slider__thumb"
      (panstart)="onPanStart()"
      (pan)="onPanLower($event)"
      (panend)="onPanEnd()"
    ></div>
    <div class="mcrai-slider__thumb-value" [class.mcrai-slider__thumb-value--active]="controlLower?.value">
      {{ controlLower?.value }}
    </div>
  </div>
  <div class="mcrai-slider__thumb-container" [style.left.px]="trackLength + trackOffset">
    <div
      class="mcrai-slider__thumb"
      (panstart)="onPanStart()"
      (pan)="onPanUpper($event)"
      (panend)="onPanEnd()"
    ></div>
    <div class="mcrai-slider__thumb-value" [class.mcrai-slider__thumb-value--active]="controlUpper?.value">
      {{ controlUpper?.value }}
    </div>
  </div>
</div>

`,
  styles: [`.mcrai-slider{display:block;padding:0 20px}.mcrai-slider__container{position:relative}.mcrai-slider__track-container{position:relative;height:40px}.mcrai-slider__track{position:absolute;top:0;bottom:0;left:0;height:2px;margin:auto}.mcrai-slider__track--background{width:100%;z-index:1}.mcrai-slider__track--fill{z-index:2}.mcrai-slider__thumb-container{position:absolute;top:0;bottom:0;z-index:2;margin:auto;display:flex;flex-direction:column;justify-content:center;align-items:center;-webkit-transform:translate(-50%,-11px);transform:translate(-50%,-11px)}.mcrai-slider__thumb{width:20px;height:20px;border-radius:10px;cursor:pointer;-webkit-transform-origin:center;transform-origin:center;-webkit-transform:scale(.7);transform:scale(.7);transition:-webkit-transform .2s;transition:transform .2s;transition:transform .2s,-webkit-transform .2s;order:2}.mcrai-slider__thumb:hover{-webkit-transform:scale(1);transform:scale(1)}.mcrai-slider__thumb-value{min-width:30px;height:20px;padding:0 5px;border-radius:4px;-webkit-transform-origin:center;transform-origin:center;display:flex;justify-content:center;align-items:center;font-size:12px;opacity:0;margin-bottom:2px;transition:opacity .2s}.mcrai-slider__thumb:hover+.mcrai-slider__thumb-value--active{opacity:1}`],
  host: {
    '[class.mcrai-slider]': 'true'
  },
  encapsulation: ViewEncapsulation.None,
  preserveWhitespaces: false
})
export class SliderComponent implements OnInit, OnChanges, OnDestroy {
  private _destroy: Subject<null> = new Subject<null>();

  @ViewChild('container', { read: ElementRef }) private _container: ElementRef;

  private _stepsCount: number;
  private _lowerIndex: number;
  private _upperIndex: number;

  @Input() min: number;
  @Input() max: number;
  @Input() step: number;
  @Input() steps: number[];
  @Input() controlLower: FormControl;
  @Input() controlUpper: FormControl;

  get sliderWidth(): number {
    if (this._isDefined(this._container.nativeElement)) {
      return this._container.nativeElement.getBoundingClientRect().width;
    } else {
      return 0;
    }
  }

  get stepWidth(): number {
    if (this._isDefined(this._stepsCount)) {
      return this.sliderWidth / this._stepsCount;
    } else {
      return 0;
    }
  }

  get trackLength(): number {
    if (this._isDefined(this._lowerIndex) && this._isDefined(this._upperIndex)) {
      return (this._upperIndex - this._lowerIndex) * this.stepWidth;
    } else {
      return 0;
    }
  }

  get trackOffset(): number {
    if (this._lowerIndex) {
      return this._lowerIndex * this.stepWidth;
    } else {
      return 0;
    }
  }

  get trackStyles(): { [key: string]: string } {
    return {
      transform: `translateX(${this.trackOffset}px)`,
      width: `${this.trackLength}px`
    }
  }

  private _value: SliderValue;
  set value(value: SliderValue) {
    this._value = value;
  }
  get value(): SliderValue {
    return this._value;
  }

  constructor() { }

  ngOnInit() { }

  ngOnChanges(changes: SimpleChanges) {
    this._setup();
  }

  ngOnDestroy() {
    this._destroy.next();
  }

  private _isDefined(value: any): boolean {
    return value !== null && typeof value !== 'undefined';
  }

  private _setup(): void {
    this._destroy.next();

    if (this._isDefined(this.steps) && Array.isArray(this.steps)) {
      this.steps.sort();
      this._stepsCount = this.steps.length - 1;
      this.min = this.steps[0];
      this.max = this.steps[this._stepsCount];
    } else {
      this._stepsCount = Math.ceil((this.max - this.min) / this.step);
    }

    if (!this.controlLower.value) {
      this.controlLower.patchValue(this.min);
    }

    if (!this.controlUpper.value) {
      this.controlUpper.patchValue(this.max);
    }

    this._lowerIndex = this._valueToIndex(this.controlLower.value);
    this._upperIndex = this._valueToIndex(this.controlUpper.value);

    this.controlLower.valueChanges
      .pipe(
        takeUntil(this._destroy),
        debounceTime(300)
      )
      .subscribe(value => this._lowerIndex = this._valueToIndex(value));

    this.controlUpper.valueChanges
      .pipe(
        takeUntil(this._destroy),
        debounceTime(300)
      )
      .subscribe(value => this._upperIndex = this._valueToIndex(value));
  }

  private _calculateIndex(x: number): number {
    const rect = this._container.nativeElement.getBoundingClientRect();

    return Math.ceil((x - rect.left) / this.stepWidth);
  }

  private _calculateLowerIndex(x: number): number {
    const index = this._calculateIndex(x);

    switch (true) {
      case index < 0:
        return 0;
      case index >= this._upperIndex - 1:
        return this._upperIndex - 1;
      default:
        return index;
    }
  }

  private _calculateUpperIndex(x: number): number {
    const index = this._calculateIndex(x);

    switch (true) {
      case index <= this._lowerIndex + 1:
        return this._lowerIndex + 1;
      case index > this._stepsCount:
        return this._stepsCount;
      default:
        return index;
    }
  }

  private _indexToValue(index: number): number {
    return Array.isArray(this.steps) ? this.steps[index] : this.step * index + this.min;
  }

  private _valueToIndex(value: number): number {
    return Array.isArray(this.steps) ? this.steps.findIndex(_value => _value === value) : Math.ceil((value - this.min) / this.step);
  }

  public onPanLower(event): void {
    this._lowerIndex = this._calculateLowerIndex(event.center.x);

    this.controlLower.patchValue(this._indexToValue(this._lowerIndex));
  }

  public onPanUpper(event): void {
    this._upperIndex = this._calculateUpperIndex(event.center.x);

    this.controlUpper.patchValue(this._indexToValue(this._upperIndex));
  }

  public onPanStart(): void { }

  public onPanEnd(): void { }

}
